/**
 * Finding a note in its chapter as the text changes (decision 58; sync case table C7; part 19e log, tables AR and NO1;
 * part 39 log, table AC). A note keeps the range, quote and context it was stored with and is never rewritten as the text
 * changes; the viewing device finds it on the fly: where the diff from the text it was made in maps it, when that text is
 * known (a reading link's snapshot) and the quote is there; else context, quote and context together nearest that place
 * (or the stored one), else the quote alone nearest it, else the quote rewritten within a few edits (fuzzy), else it is
 * detached. White space compares as one space (part 23c, AN2).
 * Ranges are in the chapter's visible text (part 23c, AN1: part 04's visible blocks joined by line ends); the source
 * range of one is what the editor marks and selects (AR9). Pure.
 */
import { CONTEXT, collapse, MAX_QUOTE, whole } from '../comments/anchor.ts';
import type { ScanBudget } from '../comments/anchor.ts';
import { sourceRange, visibleBlocks } from '../literal/visible.ts';
import type { VisibleBlock } from '../literal/visible.ts';
import { parseChapter } from '../parser/parse.ts';
import type { ParsedChapter } from '../parser/parse.ts';
import type { Alignment } from './diff.ts';
import { fuzzyBound, fuzzyCost, fuzzyFind } from './fuzzy.ts';

/**
 * AC4, AC6: how far each side of the place to be nearest a fuzzy search reads, in units of the text: a paragraph or two,
 * as a rewritten passage seldom moves far as well, and the diff puts a reader's note at its place; wider, 150 rewritten
 * notes in the 30k-word chapter would miss decision 14's chapter switch (the bench).
 */
export const FUZZY_WINDOW = 1_000;

/** A range of a chapter's visible text, or of its source: from `from` to `to`, `from` ≤ `to`. */
export interface TextRange {
  readonly from: number;
  readonly to: number;
}

/** Where a note sits as it was stored (part 19e, table NR): its range then (null: never found), its quote and context. */
export interface NotePlace {
  readonly range: TextRange | null;
  readonly quote: string;
  readonly before: string;
  readonly after: string;
}

/** One chapter's text as the notes are found in it: its visible text, blocks, and that text with white space as one. */
export class ChapterPlaces {
  /** AN1: the visible blocks joined by line ends. */
  readonly visible: string;
  readonly #blocks: readonly VisibleBlock[];
  /** Where each block starts in {@link visible}. */
  readonly #starts: readonly number[];
  /** AN2: {@link visible} with white space as one space, and each of its units' index in {@link visible}. */
  readonly #text: string;
  readonly #map: number[] = [];
  /** {@link #text}'s stretches, indexed when a note is first searched for. */
  #grams: Grams | null = null;

  /** The chapter `chapter` (parsed: the editor keeps it so, part 09's E2). */
  constructor(chapter: ParsedChapter) {
    this.#blocks = visibleBlocks(chapter);
    const starts: number[] = [];
    let at = 0;
    for (const block of this.#blocks) {
      starts.push(at);
      at += block.text.length + 1;
    }
    this.#starts = starts;
    this.visible = this.#blocks.map((block) => block.text).join('\n');
    this.#text = collapse([this.visible], this.#map).text;
  }

  /** The chapter whose source is `source`. */
  static of(source: string): ChapterPlaces {
    return new ChapterPlaces(parseChapter(source));
  }

  /**
   * NO1: the visible range a selection of the source from `from` to `to` covers: every visible character whose source
   * overlaps it; null when it covers none (only markup, or nothing chosen).
   */
  visibleOf(from: number, to: number): TextRange | null {
    let first = -1;
    let last = -1;
    this.#blocks.forEach((block, index) => {
      if (block.to <= from || block.from >= to) return;
      for (let unit = 0; unit < block.text.length; unit += 1) {
        const start = Number(block.spans[2 * unit]);
        const end = Number(block.spans[2 * unit + 1]);
        if (end <= from || start >= to) continue;
        const at = Number(this.#starts[index]) + unit;
        if (first < 0) first = at;
        last = at;
      }
    });
    return first < 0 ? null : widen(this.visible, { from: first, to: last + 1 });
  }

  /**
   * AR9: the source range of the visible range `range`: from the start of its first character to the end of its last;
   * an empty one the point before its character (or after the last), a line end between blocks the end of the block before.
   */
  sourceOf(range: TextRange): TextRange {
    const from = this.#sourcePoint(range.from, 'start');
    return range.to === range.from ? { from, to: from } : { from, to: Math.max(from, this.#sourcePoint(range.to, 'end')) };
  }

  /** The source position at visible offset `at`: the start of the character there, or the end of the one before. */
  #sourcePoint(at: number, side: 'start' | 'end'): number {
    const index = this.#blockAt(at);
    const block = this.#blocks[index];
    if (block === undefined) return 0;
    // The block's start is at or before `at` (`#blockAt`), so the offset is never negative.
    const offset = Math.min(at - Number(this.#starts[index]), block.text.length);
    if (side === 'end' && offset > 0) return sourceRange(block, offset - 1, offset).to;
    return sourceRange(block, offset, offset).from;
  }

  /** The block holding visible offset `at` (a line end between two belongs to the one before; past the end, the last). */
  #blockAt(at: number): number {
    let index = 0;
    for (let next = 1; next < this.#starts.length && Number(this.#starts[next]) <= at; next += 1) index = next;
    return index;
  }

  /**
   * NO1, NO6, NO7: the place a note has at the visible range `range` now: the range, its quote (cut at `MAX_QUOTE`) and
   * `CONTEXT` units each side, never half a surrogate pair (AN6).
   */
  placeAt(range: TextRange): NotePlace & { readonly range: TextRange } {
    const { visible } = this;
    return {
      range: { from: range.from, to: range.to },
      quote: whole(visible.slice(range.from, Math.min(range.to, range.from + MAX_QUOTE))),
      before: whole(visible.slice(Math.max(0, range.from - CONTEXT), range.from)),
      after: whole(visible.slice(range.to, range.to + CONTEXT)),
    };
  }

  /**
   * AR1–AR4, AR6, AR8, AC1–AC5: where `place` is now, as a visible range, or null when it is detached (its quote nowhere
   * within the fuzzy bound, or the budget spent). `alignment` maps the visible text the note was made in (its base) onto
   * this chapter's, when that text is known.
   */
  locate(place: NotePlace, budget: ScanBudget, alignment: Alignment | null = null): TextRange | null {
    const before = collapse([place.before]).text.slice(-CONTEXT);
    const after = collapse([place.after]).text.slice(0, CONTEXT);
    const spaced = collapse([place.quote]).text;
    const quote = spaced.trim();
    const mapped = place.range === null || alignment === null ? null : { from: alignment.map(place.range.from, 'start'), to: alignment.map(place.range.to, 'end') };
    // AC1: the quote where the diff puts it: that occurrence, of any number of equal ones.
    if (mapped !== null && quote !== '' && mapped.from < mapped.to && this.#holds(mapped, place.quote, quote)) return widen(this.visible, mapped);
    const near = mapped?.from ?? place.range?.from;
    const hint = near === undefined ? null : this.#collapsedAt(near);
    const found = quote === '' ? this.#point(before, after, hint, budget) : (this.#quote(before, spaced, after, hint, budget) ?? this.#fuzzy(quote, hint, budget));
    return found === null ? null : widen(this.visible, found);
  }

  /** AC1: whether the visible range `range` reads as the quote (`quote`, white space as one and trimmed; `raw` as stored). */
  #holds(range: TextRange, raw: string, quote: string): boolean {
    const there = collapse([this.visible.slice(range.from, range.to)]).text.trim();
    // A quote cut at MAX_QUOTE (AN6; a unit less where the cut fell inside a pair) is the start of what the range holds.
    return raw.length >= MAX_QUOTE - 1 ? there.startsWith(quote) : there === quote;
  }

  /**
   * AC4, AC6: the quote rewritten: the stretch fewest edits from it (at most a quarter of its length), nearest `hint`
   * within {@link FUZZY_WINDOW} of it (the whole text with none), within the budget; null when none or too short to search.
   */
  #fuzzy(quote: string, hint: number | null, budget: ScanBudget): TextRange | null {
    const bound = fuzzyBound(quote.length);
    if (bound === null) return null;
    const text = this.#text;
    const from = hint === null ? 0 : Math.max(0, hint - FUZZY_WINDOW);
    const to = hint === null ? text.length : Math.min(text.length, hint + quote.length + FUZZY_WINDOW);
    if (!budget.take(fuzzyCost(to - from, quote.length))) return null;
    const match = fuzzyFind(text, quote, bound, from, to, hint ?? 0);
    if (match === null) return null;
    return { from: this.#at(match.from), to: this.#at(match.to - 1) + 1 };
  }

  /** The first unit of the collapsed text at or after visible offset `at`. */
  #collapsedAt(at: number): number {
    let low = 0;
    let high = this.#map.length;
    while (low < high) {
      const middle = (low + high) >>> 1;
      if (Number(this.#map[middle]) < at) low = middle + 1;
      else high = middle;
    }
    return low;
  }

  /** The visible index of the collapsed text's unit `at`, or the visible text's end past the last. */
  #at(at: number): number {
    return this.#map[at] ?? this.visible.length;
  }

  /**
   * AR1, AR2, AR8: where `needle` is in the collapsed text, `offset` units in, nearest `hint` (null: the first), within the
   * budget; -1 when not.
   */
  #nearest(needle: string, offset: number, hint: number | null, budget: ScanBudget): number {
    if (!budget.take(this.#text.length)) return -1;
    this.#grams ??= new Grams(this.#text);
    let best = -1;
    for (const at of this.#grams.find(needle, hint === null)) {
      if (hint === null || best < 0 || Math.abs(at + offset - hint) < Math.abs(best + offset - hint)) best = at;
    }
    return best;
  }

  /** AR1, AR2: a quote's range: context, quote and context found together, else the quote alone. */
  #quote(before: string, spaced: string, after: string, hint: number | null, budget: ScanBudget): TextRange | null {
    const quote = spaced.trim();
    const joined = collapse([before, spaced, after]);
    // The quote's place in the joined probe, a space at its edge (its own, or a run from the context before) left out.
    let start = Number(joined.starts[1]);
    if (joined.text[start] === ' ') start += 1;
    let at = this.#nearest(joined.text, start, hint, budget);
    if (at < 0) {
      start = 0;
      at = this.#nearest(quote, 0, hint, budget);
      if (at < 0) return null;
    }
    const from = at + start;
    return { from: this.#at(from), to: this.#at(from + quote.length - 1) + 1 };
  }

  /** AR3, AR6: a point: between both contexts found together, else after the one before, else before the one after. */
  #point(before: string, after: string, hint: number | null, budget: ScanBudget): TextRange | null {
    const joined = collapse([before, after]);
    const tries: (readonly [string, number])[] = [];
    if (joined.text.trim() !== '') tries.push([joined.text, Number(joined.starts[1])]);
    if (before.trim() !== '') tries.push([before, before.length]);
    if (after.trim() !== '') tries.push([after, 0]);
    // AR6: a point with no text around it: the chapter's start.
    if (tries.length === 0) return { from: 0, to: 0 };
    for (const [needle, offset] of tries) {
      const at = this.#nearest(needle, offset, hint, budget);
      if (at < 0) continue;
      const point = this.#at(at + offset);
      return { from: point, to: point };
    }
    return null;
  }
}

/** Needles shorter than this are found by reading the text; longer ones through a {@link Grams} index. */
export const GRAM = 8;

/**
 * AR1, AR2, AR8: every {@link GRAM}-unit stretch of a text, bucketed by hash, so a needle's places are read from the
 * bucket of its stretch with fewest entries rather than the whole text: 200 notes read the 30k-word chapter 250 times
 * over, a third of decision 14's chapter switch (the bench).
 */
export class Grams {
  readonly #text: string;
  readonly #shift: number;
  /** Where each bucket's entries start in {@link #entries}, and after the last, their count. */
  readonly #starts: Int32Array;
  /** The start of every stretch, bucket by bucket, in text order within each. */
  readonly #entries: Int32Array;

  constructor(text: string) {
    this.#text = text;
    const count = Math.max(0, text.length - GRAM + 1);
    let bits = 10;
    while (1 << bits < count >> 2) bits += 1;
    this.#shift = 32 - bits;
    const buckets = this.#buckets(text, count);
    const starts = new Int32Array((1 << bits) + 1);
    for (let at = 0; at < count; at += 1) {
      const bucket = Number(buckets[at]) + 1;
      starts[bucket] = Number(starts[bucket]) + 1;
    }
    for (let bucket = 1; bucket < starts.length; bucket += 1) starts[bucket] = Number(starts[bucket]) + Number(starts[bucket - 1]);
    // Each bucket's next free entry, filled in text order.
    const next = starts.slice();
    const entries = new Int32Array(count);
    for (let at = 0; at < count; at += 1) {
      const bucket = Number(buckets[at]);
      const entry = Number(next[bucket]);
      entries[entry] = at;
      next[bucket] = entry + 1;
    }
    this.#starts = starts;
    this.#entries = entries;
  }

  /** The bucket of each of `text`'s first `count` stretches: a hash rolled a unit at a time, its high bits mixed down. */
  #buckets(text: string, count: number): Int32Array {
    // The first unit's weight in a stretch's hash, 31 to the power GRAM - 1, which leaves as the stretch moves on.
    let weight = 1;
    for (let unit = 1; unit < GRAM; unit += 1) weight = Math.imul(weight, 31);
    const out = new Int32Array(count);
    const shift = this.#shift;
    let hash = 0;
    for (let unit = 0; unit < GRAM - 1 && unit < text.length; unit += 1) hash = (Math.imul(hash, 31) + text.charCodeAt(unit)) | 0;
    for (let at = 0; at < count; at += 1) {
      hash = (Math.imul(hash, 31) + text.charCodeAt(at + GRAM - 1)) | 0;
      out[at] = Math.imul(hash, 0x9e3779b1) >>> shift;
      hash = (hash - Math.imul(text.charCodeAt(at), weight)) | 0;
    }
    return out;
  }

  /** Every place `needle` (not empty) starts in the text, in order, overlapping ones too; with `first`, only the first. */
  find(needle: string, first: boolean): number[] {
    const text = this.#text;
    const found: number[] = [];
    if (needle.length < GRAM) {
      for (let at = text.indexOf(needle); at >= 0 && !(first && found.length > 0); at = text.indexOf(needle, at + 1)) found.push(at);
      return found;
    }
    // The needle's stretch whose bucket holds fewest; every place it starts has that stretch `offset` units in.
    let bucket = 0;
    let offset = 0;
    let least = Infinity;
    this.#buckets(needle, needle.length - GRAM + 1).forEach((each, at) => {
      const size = Number(this.#starts[each + 1]) - Number(this.#starts[each]);
      if (size < least) [bucket, offset, least] = [each, at, size];
    });
    for (let entry = Number(this.#starts[bucket]); entry < Number(this.#starts[bucket + 1]); entry += 1) {
      const at = Number(this.#entries[entry]) - offset;
      if (at >= 0 && text.startsWith(needle, at)) {
        found.push(at);
        if (first) break;
      }
    }
    return found;
  }
}

/** AN6: `range` widened to whole characters: never between the halves of a surrogate pair. */
function widen(text: string, range: TextRange): TextRange {
  const low = (at: number): boolean => /[\uDC00-\uDFFF]/u.test(text.charAt(at)) && /[\uD800-\uDBFF]/u.test(text.charAt(at - 1));
  const from = low(range.from) ? range.from - 1 : range.from;
  return { from, to: Math.max(from, low(range.to) ? range.to + 1 : range.to) };
}
